/* ================================================================
   LFJ PAGE TRANSITION — Design Checkpoint 2 (B4), updated CP27
   ----------------------------------------------------------------
   Applies the TV-static / loading / reveal beat to every internal
   navigation site-wide — a real multi-page Shopify theme, no
   client-side router.

   OWNERSHIP SPLIT WITH lfj-intro.js:
   - Menu screen (data-room="menu") opts OUT of the incoming reveal
     here — it already has its own more elaborate version via
     lfj-intro.js. This script's CSS default-visible rule is scoped
     to `body:not([data-room="menu"])` for exactly that reason.
   - The OUTGOING click-intercept runs on every page including the
     Menu screen, since leaving the Menu is a real navigation.

   CP27 ADDITIONS (additive — original two-phase reveal intact):
   ─────────────────────────────────────────────────────────────────
   OUTGOING SEQUENCE (replacing the plain lfj-tv-off-seq):
     click → static visual (#lfj-tv-static) → static noise (Web Audio)
     → beep (Web Audio) → black (#lfj-page-exit) → navigate

   INCOMING SEQUENCE (after loading window completes):
     black loading screen → lfj-tv-on-seq (existing) → static flash
     → static blends out → content fade-in (two-phase, unchanged)

   RANDOM LOADING SCREEN VARIANTS:
     Each navigation randomly picks one of four loading screen styles
     applied via data-lv on #lfj-page-enter. Every variant shares one
     constant: a dancing, multicolor LFJ wordmark (injected by JS so
     the animation resets cleanly on each navigation).

   FAILURE MODE: if #lfj-tv-static is absent, or Web Audio throws,
   or the static overlay can't be shown, original black/reveal
   behavior is the fallback — nothing ever stays invisible forever.
   The loadMs+2000ms absolute safety net is preserved unchanged.
================================================================ */
(function () {
  'use strict';

  if (window.__lfjPageTransitionBound) return;
  window.__lfjPageTransitionBound = true;

  var CFG = (window.LF_CONFIG && window.LF_CONFIG.intro) || {};
  var loadMs = Math.max(1000, Math.min(3000, Number(CFG.loadMs) || 1800));
  var reducedMotion = false;
  try {
    reducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* CP28 — matches this codebase's existing safeRun() isolation
     pattern (see lfj-lfj.js). A thrown error inside one onReady
     callback used to be able to abort the rest of this file's
     top-level execution — including the absolute safety-net
     setTimeout and the pageshow listener defined further down —
     since everything in this IIFE runs as one synchronous function
     body. Wrapping each stage so a failure in one can never take out
     the safety nets meant to catch exactly that kind of failure. */
  function safeRun(label, fn) {
    try { fn(); } catch (e) {
      try { console.error('[lfj-page-transition] ' + label + ' failed:', e); } catch (e2) {}
    }
  }

  /* ══════════════════════════════════════════════════════════════
     WEB AUDIO — synthesized TV static + 8-bit beep
     Pattern from lf-ambient-mix.js: procedural synthesis,
     no external asset needed. Fails silent on any error.
  ══════════════════════════════════════════════════════════════ */
  function getAudioCtx() {
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!window.__lfjSharedAudioCtx) {
        window.__lfjSharedAudioCtx = new AC();
      }
      var ctx = window.__lfjSharedAudioCtx;
      if (ctx.state === 'suspended') ctx.resume().catch(function () {});
      return ctx;
    } catch (e) { return null; }
  }

  function playStaticNoise(durationMs) {
    try {
      var ctx = getAudioCtx();
      if (!ctx) return;
      var n = Math.ceil(ctx.sampleRate * (durationMs / 1000));
      var buf = ctx.createBuffer(1, n, ctx.sampleRate);
      var ch = buf.getChannelData(0);
      for (var i = 0; i < n; i++) ch[i] = (Math.random() * 2 - 1);
      var src = ctx.createBufferSource();
      src.buffer = buf;
      /* Bandpass makes it read as TV static hiss, not flat noise */
      var bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 3200;
      bp.Q.value = 0.6;
      var gain = ctx.createGain();
      var now = ctx.currentTime;
      gain.gain.setValueAtTime(0.26, now);
      gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
      src.connect(bp); bp.connect(gain); gain.connect(ctx.destination);
      src.start();
    } catch (e) { /* fail silent */ }
  }

  function playBeep() {
    try {
      var ctx = getAudioCtx();
      if (!ctx) return;
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      /* Square wave → classic 8-bit console beep */
      osc.type = 'square';
      osc.frequency.value = 880; /* A5 */
      var now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.20, now + 0.012);
      gain.gain.setValueAtTime(0.20, now + 0.075);
      gain.gain.linearRampToValueAtTime(0, now + 0.13);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(now + 0.14);
    } catch (e) { /* fail silent */ }
  }

  /* CP28 — "mystery" loading variant's jingle. Direct ask was for a
     loading screen that "plays a certain pokemon song... in 8bit
     filter type sound." Actual Pokemon music is copyrighted and
     can't be reproduced or closely imitated — what plays here is a
     short original 5-note chiptune motif (square-wave arpeggio,
     ~700ms), evoking the same "surprise 8-bit jingle" feeling
     without reproducing anyone's actual composition. One-shot, not
     looped — plays once when the mystery variant is chosen. */
  function playMysteryJingle() {
    try {
      var ctx = getAudioCtx();
      if (!ctx) return;
      var notes = [523.25, 659.25, 783.99, 659.25, 987.77]; /* C5 E5 G5 E5 B5 — original motif */
      var start = ctx.currentTime + 0.05;
      var step = 0.11;
      notes.forEach(function (freq, i) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = freq;
        var t = start + i * step;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.16, t + 0.015);
        gain.gain.setValueAtTime(0.16, t + step * 0.6);
        gain.gain.linearRampToValueAtTime(0, t + step * 0.95);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(t); osc.stop(t + step);
      });
    } catch (e) { /* fail silent */ }
  }

  /* ══════════════════════════════════════════════════════════════
     TV STATIC OVERLAY helpers
  ══════════════════════════════════════════════════════════════ */
  function getStaticEl() {
    return document.getElementById('lfj-tv-static');
  }

  function showStatic() {
    var el = getStaticEl();
    if (!el) return false;
    el.classList.remove('is-blending-out');
    el.classList.add('is-on');
    return true;
  }

  function blendOutStatic(cb) {
    var el = getStaticEl();
    if (!el) { if (cb) cb(); return; }
    el.classList.remove('is-on');
    el.classList.add('is-blending-out');
    var done = false;
    var finish = function () {
      if (done) return; done = true;
      el.classList.remove('is-blending-out');
      if (cb) cb();
    };
    el.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 500); /* safety net if transitionend never fires */
  }

  /* ══════════════════════════════════════════════════════════════
     RANDOM LOADING SCREEN VARIANTS
     Applied to #lfj-page-enter each navigation via data-lv.
     CSS for each variant lives in lfj-pixel.css.
     The dancing LFJ mark is injected fresh each navigation so its
     animation resets cleanly (not carried over from a prior visit).

     CP28 — added "wave" (new visual) and "mystery" (new visual +
     one-shot jingle). "mystery" is deliberately weighted rare (see
     WEIGHTED_POOL below) rather than equal odds with the rest — a
     "mystery" screen that shows up 1-in-6 times doesn't read as a
     surprise, it reads as just another variant.
  ══════════════════════════════════════════════════════════════ */
  var WEIGHTED_POOL = (window.LFJ_LV_POOL||['grid','static','psycho']);

  function applyRandomLoadingVariant(enterEl) {
    if (!enterEl) return;
    var egg = window.LFJ_LV_EGG || 'mystery';
    var v = (enterEl.id === 'lfj-page-enter' && window.LFJ_LV_CHOSEN && !enterEl.__lvDone)
      ? window.LFJ_LV_CHOSEN
      : (Math.random() < (window.LFJ_LV_EGG_ODDS || 0.12) ? egg : WEIGHTED_POOL[Math.floor(Math.random() * WEIGHTED_POOL.length)]);
    enterEl.__lvDone = true;
    enterEl.setAttribute('data-lv', v);
    /* Replace static loading-mark with the dancing multicolor wordmark.
       The three data-di attributes drive per-letter animation stagger
       (--di CSS custom property, defined in lfj-pixel.css). */
    var mark = enterEl.querySelector('.lfj-loading-mark');
    if (mark) {
      mark.className = 'lfj-loading-mark lfj-dancing-mark';
      mark.innerHTML =
        '<span class="lfj-dance-letter" style="--di:0">L</span>' +
        '<span class="lfj-dance-letter" style="--di:1">F</span>' +
        '<span class="lfj-dance-letter" style="--di:2">J</span>';
    }
    if (v === 'mystery') safeRun('mystery jingle', playMysteryJingle);
  }

  /* ══════════════════════════════════════════════════════════════
     INCOMING: loading window → static flash → blend out → reveal
     Original two-phase reveal (animationend + safety-net timeout)
     is preserved intact — static inserts between the TV-on anim
     and the final content fade-in, never before or during it.
  ══════════════════════════════════════════════════════════════ */
  onReady(function () {
    safeRun('incoming reveal', function () {
    var enter = document.getElementById('lfj-page-enter');
    if (!enter) { document.body.classList.add('lfj-content-ready'); return; }
    if (document.body.getAttribute('data-room') === 'menu') {
      document.body.classList.add('lfj-content-ready');
      return;
    }

    /* Random variant is applied before the loading screen is even seen —
       the CSS default-visible rule already shows it, so the variant is live
       from the very first frame the page paints. */
    applyRandomLoadingVariant(enter);

    var fill = document.getElementById('lfj-page-enter-fill');
    if (fill) {
      enter.style.setProperty('--lfj-load-ms', loadMs + 'ms');
      /* Loading-screen flash fix — this exact pattern already exists
         in lfj-intro.js (Menu screen) but was never mirrored here.
         Changing --lfj-load-ms on an element whose animation is
         already running (default 1800ms fallback fires the instant
         this overlay paints, before DOMContentLoaded) can make the
         browser recompute the animation mid-flight instead of
         restarting cleanly — reads as a brief "reset" flash on the
         bar. Forcing a reflow between clearing and restoring the
         animation makes it restart from frame zero at the correct
         duration every time, same as the Menu screen already does. */
      fill.style.animation = 'none';
      fill.offsetHeight; /* force reflow */
      fill.style.animation = '';
    }

    function revealContent() {
      document.body.classList.add('lfj-content-ready');
      var _e = document.getElementById('lfj-page-enter');
      if (_e) { _e.style.opacity='0'; _e.style.pointerEvents='none'; _e.style.visibility='hidden'; }
      window.LFJ_LOAD_DONE = true; try { window.dispatchEvent(new CustomEvent('lf:page:reveal')); } catch(e) {}
    }

    var wait = reducedMotion ? Math.min(loadMs, 800) : loadMs;

    /* CP83 — never reveal before the page is truly loaded: after the timed wait, if the document (and fonts) are
       still loading, the loader stays up (the bar sits full) until window "load", capped at 7s. */
    function whenLoaded(cb) {
      var done = false;
      function go() { if (done) return; done = true; cb(); }
      if (document.readyState === 'complete') { go(); return; }
      window.addEventListener('load', function () {
        var f = document.fonts && document.fonts.ready;
        if (f && f.then) f.then(go, go); else go();
      }, { once: true });
      setTimeout(go, 7000);
    }
    setTimeout(function () { whenLoaded(function () {
      safeRun('incoming reveal (post-wait)', function () {
      enter.classList.add('is-hiding');

      if (reducedMotion) { revealContent(); return; }

      /* Wait for the TV-on animation on #lfj-page-enter to finish,
         then flash static briefly before revealing content. */
      var animDone = false;
      var onTvOnEnd = function () {
        if (animDone) return; animDone = true;
        safeRun('static flash before reveal', function () {
          /* Try static: if overlay exists, flash it then blend out into content */
          var hasStatic = showStatic();
          if (hasStatic) {
            setTimeout(function () {
              safeRun('blend out static', function () { blendOutStatic(revealContent); });
            }, 240);
          } else {
            /* No static overlay — reveal immediately (original fallback) */
            revealContent();
          }
        });
      };
      /* CP30: fixed 450ms — avoids child animationend bubble race */
      setTimeout(onTvOnEnd, 450);
      });
    }); }, wait);
    });
  });

  /* ══════════════════════════════════════════════════════════════
     OUTGOING: static visual + sounds → black → navigate
     Replaces the original plain "add is-playing, wait 480ms, nav."
  ══════════════════════════════════════════════════════════════ */
  onReady(function () {
    var exit = document.getElementById('lfj-page-exit');
    if (!exit) return;

    function playOutgoingSequence(href) {
      if (reducedMotion) {
        window.location.href = href;
        return;
      }

      /* CP28 — guarantee navigation happens no matter what. If
         anything below throws (Web Audio, DOM query, whatever),
         the user must never be left stuck on a black/static screen
         with preventDefault() already called on their click and no
         way forward. This mirrors the CSS-only ceilings added above
         for the two overlays — same principle, JS side. */
      var navigated = false;
      function goNow() {
        if (navigated) return;
        navigated = true;
        window.location.href = href;
      }
      setTimeout(goNow, 1400); /* absolute ceiling, well past the normal 690ms */

      try {
        /* CP83 — pause everything (nothing keeps playing under the transition) and FADE to black so the
           old page is never visible again before the loading screen takes over. */
        safeRun('pause media', function () {
          [].forEach.call(document.querySelectorAll('audio,video'), function (m) { try { m.pause(); } catch (e) {} });
        });
        safeRun('fade layer', function () {
          var f = document.createElement('div');
          f.id = 'lfj-fade-out';
          f.style.cssText = 'position:fixed;inset:0;background:#000;opacity:0;z-index:9350;pointer-events:none;transition:opacity 260ms ease-out';
          document.body.appendChild(f);
          f.offsetHeight; /* reflow so the transition runs */
          f.style.opacity = '1';
        });
        /* 1 ── Static visual appears immediately */
        var hasStatic = showStatic();

        /* 2 ── Static noise starts simultaneously */
        playStaticNoise(340);

        /* 3 ── Beep at 175ms (while static is showing, after noise peak) */
        setTimeout(function () { safeRun('outgoing beep', playBeep); }, 175);

        /* 4 ── At 350ms: blend out static, black exit overlay takes over */
        setTimeout(function () {
          safeRun('outgoing black overlay', function () {
            if (hasStatic) {
              blendOutStatic(null); /* blend it toward black */
            }
            exit.classList.add('is-playing');
          });
        }, 350);

        /* 5 ── Navigate at 690ms — exit anim is 450ms from its start point
                at 350ms, so this is 340ms after it starts = fully covered */
        setTimeout(goNow, 690);
      } catch (e) {
        try { console.error('[lfj-page-transition] outgoing sequence failed:', e); } catch (e2) {}
        goNow();
      }
    }

    /* CP29 — LIVE BUG FIX. Nothing was stopping a second click on any
       link (even the SAME link) from re-running this entire sequence
       and re-assigning window.location.href a second time. Repeatedly
       assigning location.href while a navigation is already in
       flight can cancel and restart it in some browsers — so an
       impatient extra click during the normal load wait (real
       network time, not JS timing) could keep resetting progress
       back to zero. Reported live: "clicked FM 7 times... the music
       played every time" — each click replaying the static+beep
       sequence is exactly what re-triggering looks and sounds like.
       Fix: once a navigation has started, every further click is a
       no-op here — the first click's own timers are already
       guaranteed to complete (see goNow's ceilings above), so there
       is never a reason for a second click to do anything but wait. */
    var navigationStarted = false;

    document.addEventListener('click', function (ev) {
      if (ev.defaultPrevented || ev.button !== 0 ||
          ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;

      var a = ev.target.closest && ev.target.closest('a[href]');
      if (!a) return;
      if (a.target && a.target !== '' && a.target !== '_self') return;
      if (a.hasAttribute('download')) return;

      var href = a.getAttribute('href') || '';
      if (!href || href.indexOf('#') === 0) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

      var url;
      try { url = new URL(a.href, window.location.href); } catch (e) { return; }
      if (url.origin !== window.location.origin) return;
      /* Same page, different hash only — not a real page change */
      if (url.pathname === window.location.pathname &&
          url.search === window.location.search && url.hash) return;

      ev.preventDefault();

      if (navigationStarted) return; /* already leaving — a repeat click changes nothing */
      navigationStarted = true;

      playOutgoingSequence(a.href);
    });
  });

  /* ══════════════════════════════════════════════════════════════
     ABSOLUTE SAFETY NET — content must NEVER stay invisible forever
  ══════════════════════════════════════════════════════════════ */
  setTimeout(function () {
    var _se = document.getElementById('lfj-page-enter');
    if (_se) { _se.style.opacity='0'; _se.style.pointerEvents='none'; _se.style.visibility='hidden'; }
    document.body.classList.add('lfj-content-ready');
    window.LFJ_LOAD_DONE = true; try { window.dispatchEvent(new CustomEvent('lf:page:reveal')); } catch(e) {}
  }, loadMs + 8500);

  /* ── bfcache safety: reset all overlays if page restored from cache ── */
  window.addEventListener('pageshow', function (ev) {
    if (!ev.persisted) return;
    var exit = document.getElementById('lfj-page-exit');
    if (exit) exit.classList.remove('is-playing');
    var enter = document.getElementById('lfj-page-enter');
    if (enter) enter.classList.add('is-hiding');
    var tvStatic = getStaticEl();
    if (tvStatic) {
      tvStatic.classList.remove('is-on');
      tvStatic.classList.remove('is-blending-out');
    }
    document.body.classList.add('lfj-content-ready');
  });
}());
